import { useFormik } from "formik"
import React, { useState } from "react"
import {
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner
} from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import * as Yup from "yup"
import ModalComponent from "../../../components/Modal"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"
import { _alphabetRegex_ } from "../../../lib/Regex"

const AddEditAmenity = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const validationSchema = Yup.object({
    amenity_name: Yup.string()
      .min(4, "Enter at least 4 character for Amenity Name ")
      .required("Amenity Name is required"),
    amenity_img: Yup.mixed()
      .test("required", "Amenity image is required", (file) => {
        if (isUpdate) {
          return file !== null && file !== undefined
        }

        return file
      })
      .test(
        "fileType",
        "File format is not supported, Please use .jpg/.png/.jpeg",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return (
            file && ["image/png", "image/jpg", "image/jpeg"].includes(file.type)
          )
        }
      )
      .test(
        "fileSize",
        "File size too large, max file size is 500 Kb",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return file && file.size <= 512000
        }
      ),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      values = {
        ...values,
        amenity_img:
          typeof values?.amenity_img === "object"
            ? values?.amenity_img
            : isUpdate.amenity_img,
      }
      const data = new FormData()
      for (const key in values) {
        data.append(key, values[key])
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateAmenity + isUpdate.id
          : admin.createAmenity,
        data: data,
      })

      if (response?.data?.code === 201 || response?.data?.code === 200) {
        toast.success(response.data.message)
        setLoading(false)
        setShow(false)
        setIsUpdate(false)
        fetchData()
        resetForm()
      } else {
        setLoading(false)
        throw { message: response?.data?.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          amenity_name: isUpdate.amenity_name,
          amenity_img: isUpdate.amenity_img?.split("/").pop(),
        }
      : {
          amenity_name: "",
          amenity_img: "",
        },

    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, setFieldValue, resetForm, initialValues } =
    formik

  return (
    <>
      <ModalComponent
        show={show}
        modalsize={"md"}
        backdrop="static"
        animationFade={true}
        onHide={() => {
          handleClose()
          resetForm()
        }}
        loading={loading}
        modeltitle={isUpdate ? "Update Amenity" : "Add Amenity"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext={"Cancel"}
        handleSubmit={formik.handleSubmit}
        children={
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12}>
                <FloatingLabel
                  controlId="amenityname"
                  label={
                    <>
                      Amenity Name <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="amenity_name"
                    autoComplete="false"
                    maxLength={30}
                    value={values.amenity_name}
                    onChange={(e) => handleOnchange(e, _alphabetRegex_, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.amenity_name && errors.amenity_name}
                    isValid={touched.amenity_name && !errors.amenity_name}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amenity_name}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12}>
                <FloatingLabel
                  controlId="amenityimg"
                  label={
                    <>
                      Amenity Image <span className="required_star">*</span>
                    </>
                  }
                >
                  {values?.amenity_img ? (
                    <div
                      className={`previewnm_ip ${
                        touched?.amenity_img && errors?.amenity_img
                          ? "is-invalid"
                          : ""
                      }`}
                    >
                      <TooltipComponent
                        toolplace="top"
                        tooltipText={
                          typeof values?.amenity_img === "object"
                            ? values?.amenity_img.name
                            : values?.amenity_img
                        }
                      >
                        <p className="mb-0 previewnm_title">
                          {typeof values?.amenity_img === "object"
                            ? values?.amenity_img.name
                            : values?.amenity_img}
                        </p>
                      </TooltipComponent>
                      <span
                        className="remove_previewnm"
                        onClick={() => {
                          setFieldValue("amenity_img", "")
                        }}
                      >
                        <i className="icon_main cross_icon"></i>
                      </span>
                    </div>
                  ) : (
                    <Form.Control
                      type="file"
                      placeholder=""
                      autoComplete="false"
                      name="amenity_img"
                      onChange={async (e) => {
                        setFieldValue("amenity_img", e?.target?.files[0])
                      }}
                      isInvalid={touched.amenity_img && errors.amenity_img}
                      isValid={touched.amenity_img && !errors.amenity_img}
                    />
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.amenity_img}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>
          </Form>
        }
      />
    </>
  )
}

export default AddEditAmenity
